/**
 * Agent Run Service
 *
 * Generic DB read/write for agent_run_logs.
 * No agent-specific logic here — each agent type is identified by its
 * agent_type string ('atc', etc.).
 */

import { getSupabaseClient } from './supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AgentRunTriggeredBy = 'manual' | 'auto_create' | 'schedule';
export type AgentRunStatus = 'running' | 'completed' | 'failed' | 'cancelled';

/**
 * Which items a run covers.
 *
 * The first three are date-shaped: they scan the workspace and differ only in
 * how far back. `pool` and `cycle` are structural — they scan the set someone
 * is actually deciding about. That distinction is why R3 added them: a session
 * wants proposals for ITS pool, and a workspace-wide-by-date scan can only
 * offer "everything, recently", which is both more expensive and less useful
 * (Relationship Detection Assist PRD v2 §5).
 */
export type RunScopeType = 'all_empty' | 'since_last_run' | 'date_range' | 'pool' | 'cycle' | 'track' | 'collection' | 'items';

export interface RunScope {
  type: RunScopeType;
  from?: string; // ISO timestamp — used by date_range
  to?: string;   // ISO timestamp — used by date_range
  /** item_pools.id — used by `pool`. */
  poolId?: string;
  /** cycles.id — used by `cycle`; covers every pool bound to that cycle. */
  cycleId?: string;
  /** tracks.id — used by `track`; the track's item pool. */
  trackId?: string;
  /** collections.id — used by `collection`; a smart collection's resolved membership. */
  collectionId?: string;
  /** items.id — used by `items`: the rows a person chose on the items list ("Ask [agent] for these N"). */
  itemIds?: string[];
}

export interface BatchStats {
  processed: number;
  proposed: number;
  skipped: number;
  errors: number;
  /** How many items the run read from its scope; absent when the scope could not be read. */
  itemsRead?: number;
}

export interface AgentRunLog {
  id: string;
  workspaceId: string;
  agentType: string;
  triggeredBy: AgentRunTriggeredBy;
  runByUserId: string | null;
  startedAt: string;
  completedAt: string | null;
  scope: RunScope;
  stats: BatchStats;
  status: AgentRunStatus;
  errorMessage: string | null;
}

// ── Converters ────────────────────────────────────────────────────────────────

function rowToLog(row: Record<string, unknown>): AgentRunLog {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    agentType: row.agent_type as string,
    triggeredBy: row.triggered_by as AgentRunTriggeredBy,
    runByUserId: (row.run_by_user_id as string | null) ?? null,
    startedAt: row.started_at as string,
    completedAt: (row.completed_at as string | null) ?? null,
    scope: (row.scope as RunScope) ?? { type: 'all_empty' },
    stats: (row.stats as BatchStats) ?? { processed: 0, proposed: 0, skipped: 0, errors: 0 },
    status: row.status as AgentRunStatus,
    errorMessage: (row.error_message as string | null) ?? null,
  };
}

const TABLE = 'agent_run_logs';

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Insert a new run log row (status = 'running').
 * Returns the new log id.
 */
export async function createRunLog(params: {
  workspaceId: string;
  agentType: string;
  triggeredBy: AgentRunTriggeredBy;
  scope: RunScope;
  runByUserId?: string | null;
}): Promise<{ data: AgentRunLog | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        workspace_id: params.workspaceId,
        agent_type: params.agentType,
        triggered_by: params.triggeredBy,
        scope: params.scope,
        run_by_user_id: params.runByUserId ?? null,
        status: 'running',
        started_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) return { data: null, error: error.message };
    return { data: rowToLog(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Finalise a run log with stats and terminal status.
 */
export async function updateRunLog(
  id: string,
  stats: BatchStats,
  status: Exclude<AgentRunStatus, 'running'>,
  errorMessage?: string | null,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(TABLE)
      .update({
        stats,
        status,
        completed_at: new Date().toISOString(),
        error_message: errorMessage ?? null,
      })
      .eq('id', id);

    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Fetch recent run logs for an agent in a workspace.
 */
export async function getRunLogs(
  workspaceId: string,
  agentType: string,
  limit = 10,
): Promise<{ data: AgentRunLog[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('agent_type', agentType)
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) return { data: [], error: error.message };
    return {
      data: (data as Record<string, unknown>[]).map(rowToLog),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Get the most recently completed run for an agent.
 * Used to resolve the 'since_last_run' scope.
 */
export async function getLastCompletedRun(
  workspaceId: string,
  agentType: string,
): Promise<{ data: AgentRunLog | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .rpc('get_last_completed_agent_run', {
        p_workspace_id: workspaceId,
        p_agent_type: agentType,
      });

    if (error) return { data: null, error: error.message };
    const rows = data as Record<string, unknown>[] | null;
    if (!rows || rows.length === 0) return { data: null, error: null };
    return { data: rowToLog(rows[0]), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Cancel a running log row (e.g. on user abort).
 */
/**
 * The run in progress for an agent (the row the run store opened before
 * calling the batch runner), so a runner's decision records and deliveries
 * name it. Null when none is running.
 */
export async function currentAgentRunId(workspaceId: string, agentType: string): Promise<string | null> {
  const { data } = await getSupabaseClient()
    .from('agent_run_logs')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('agent_type', agentType)
    .eq('status', 'running')
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

export async function cancelRunLog(id: string): Promise<{ error: string | null }> {
  return updateRunLog(id, { processed: 0, proposed: 0, skipped: 0, errors: 0 }, 'cancelled');
}
